import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  toast,
  ToastContainer,
  ToastContent,
  ToastOptions,
  TypeOptions,
} from "react-toastify";
import { X } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "./NotificationToast.css";
import { ReceiveGroupCallInviteResponse } from "../interfaces/videoHubResponse/videoHubResponse";
import EventNotification from "../interfaces/eventNotification/EventNotification";
import IncomingCallNotification from "../components/components/notifications/IncomingCallNotification";
import ToastNotification from "../components/components/notifications/ToastNotification";
import AppToastContent, {
  AppToastType,
} from "../components/components/notifications/AppToastContent";

interface NotificationContextProps {
  notify: (message: ReactNode, options?: ToastOptions) => void;
  notifyEvent: (notification: EventNotification, options?: ToastOptions) => void;
  notifyForCall: (
    hubResponse: ReceiveGroupCallInviteResponse,
    onAccept: () => void,
    onReject: () => void,
  ) => void;
  hideCallBanner: () => void;
}

const NotificationContext = createContext<NotificationContextProps>({
  notify: () => undefined,
  notifyEvent: () => undefined,
  notifyForCall: () => undefined,
  hideCallBanner: () => undefined,
});

const DEFAULT_AUTO_CLOSE = 5000;
const LATEST_EVENT_TOAST_ID = "waivera-latest-event";
const MAX_VISIBLE_TOASTS = 3;

const BASE_TOAST_STYLE: React.CSSProperties = {
  minHeight: 0,
  padding: "12px 14px",
  border: "1px solid #e5e7eb",
  borderRadius: "16px",
  background: "#ffffff",
  color: "#111827",
  boxShadow:
    "0 18px 45px rgba(15, 23, 42, 0.14), 0 3px 10px rgba(15, 23, 42, 0.06)",
};

let activeEventGroup: {
  key: string;
  latestNotificationId: string;
  count: number;
} | null = null;

const getEventGroupKey = (notification: EventNotification) => {
  const primaryResource =
    notification.notificationResources?.find(
      (resource) => resource.resourceType === "task_action",
    ) ??
    notification.notificationResources?.find(
      (resource) => resource.resourceType === "task",
    ) ??
    notification.notificationResources?.find(
      (resource) =>
        resource.resourceType !== "user" && Boolean(resource.resourceId),
    );

  return primaryResource?.resourceId
    ? `${notification.type}:${primaryResource.resourceId}`
    : notification.type;
};

const mapToastType = (
  type?: TypeOptions | AppToastType,
): AppToastType => {
  switch (type) {
    case "success":
    case "error":
    case "warning":
    case "info":
    case "loading":
      return type;
    default:
      return "default";
  }
};

const wrapToastContent = (
  message: ToastContent,
  type: AppToastType,
): ToastContent => {
  // Preserve intentionally custom React components.
  if (React.isValidElement(message)) {
    return message;
  }

  return <AppToastContent type={type} message={message as ReactNode} />;
};

/**
 * Use our own close button instead of depending on React-Toastify's built-in
 * close button markup. React-Toastify injects closeToast into this component.
 *
 * Keeping the click handler here gives us one reliable dismissal path for
 * every standard toast and avoids global/Bootstrap button styles interfering
 * with the library's default close control.
 */
const WaiveraToastCloseButton: React.FC<{
  closeToast?: () => void;
}> = ({ closeToast }) => (
  <button
    type="button"
    className="waivera-toast-close-button"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      closeToast?.();
    }}
    aria-label="Close notification"
  >
    <X size={15} strokeWidth={2} aria-hidden="true" />
  </button>
);

const mergeToastOptions = (
  options: ToastOptions | undefined,
  type: AppToastType,
): ToastOptions => {
  const requestedCloseButton = options?.closeButton;

  return {
    ...options,
    type: type === "default" || type === "loading" ? options?.type : type,
    icon: false,
    // Respect an explicit false or a caller-provided custom close button.
    // Otherwise use our own close button with an explicit closeToast handler.
    closeButton:
      requestedCloseButton === false
        ? false
        : React.isValidElement(requestedCloseButton)
          ? requestedCloseButton
          : <WaiveraToastCloseButton />,
    autoClose:
      options?.autoClose === false
        ? false
        : options?.autoClose ?? DEFAULT_AUTO_CLOSE,
    style: {
      ...BASE_TOAST_STYLE,
      ...options?.style,
    },
    className: ["waivera-toast", options?.className]
      .filter(Boolean)
      .join(" "),
    progressClassName: [
      `waivera-toast-progress waivera-toast-progress-${type}`,
      options?.progressClassName,
    ]
      .filter(Boolean)
      .join(" "),
  };
};

/**
 * Existing parts of the application call react-toastify directly
 * (toast.success/toast.error/etc.). Keep those call sites compatible with the
 * shared Waivera appearance while routing their close buttons through the
 * explicit close handler above.
 */
let toastMethodsInstalled = false;

const installGlobalToastMethods = () => {
  if (toastMethodsInstalled) return;
  toastMethodsInstalled = true;

  const originalSuccess = toast.success.bind(toast);
  const originalError = toast.error.bind(toast);
  const originalWarning = toast.warning.bind(toast);
  const originalInfo = toast.info.bind(toast);
  const originalLoading = toast.loading.bind(toast);

  toast.success = ((message: ToastContent, options?: ToastOptions) =>
    originalSuccess(
      wrapToastContent(message, "success"),
      mergeToastOptions(options, "success"),
    )) as typeof toast.success;

  toast.error = ((message: ToastContent, options?: ToastOptions) =>
    originalError(
      wrapToastContent(message, "error"),
      mergeToastOptions(options, "error"),
    )) as typeof toast.error;

  toast.warning = ((message: ToastContent, options?: ToastOptions) =>
    originalWarning(
      wrapToastContent(message, "warning"),
      mergeToastOptions(options, "warning"),
    )) as typeof toast.warning;

  toast.info = ((message: ToastContent, options?: ToastOptions) =>
    originalInfo(
      wrapToastContent(message, "info"),
      mergeToastOptions(options, "info"),
    )) as typeof toast.info;

  toast.loading = ((message: ToastContent, options?: ToastOptions) =>
    originalLoading(
      wrapToastContent(message, "loading"),
      mergeToastOptions(
        {
          ...options,
          autoClose: options?.autoClose ?? false,
        },
        "loading",
      ),
    )) as typeof toast.loading;
};

installGlobalToastMethods();

/**
 * Event toasts have their own X button inside ToastNotification, so the
 * React-Toastify close button stays disabled. The custom X dismisses the
 * exact toast by ID rather than depending on the render callback's closeToast
 * function.
 */
export const showEventNotificationToast = (
  notification: EventNotification,
  options?: ToastOptions,
) => {
  const isExistingEventActive = toast.isActive(LATEST_EVENT_TOAST_ID);

  if (
    isExistingEventActive &&
    activeEventGroup?.latestNotificationId === notification.id
  ) {
    return LATEST_EVENT_TOAST_ID;
  }

  const groupKey = getEventGroupKey(notification);
  const groupCount =
    isExistingEventActive && activeEventGroup?.key === groupKey
      ? activeEventGroup.count + 1
      : 1;

  activeEventGroup = {
    key: groupKey,
    latestNotificationId: notification.id,
    count: groupCount,
  };

  const closeEventToast = () => {
    activeEventGroup = null;
    toast.dismiss(LATEST_EVENT_TOAST_ID);
  };

  const content = () => (
    <ToastNotification
      notification={notification}
      groupCount={groupCount}
      onClose={closeEventToast}
    />
  );

  const baseEventOptions = mergeToastOptions(
    options,
    mapToastType(notification.status),
  );
  const mergedOptions: ToastOptions = {
    ...baseEventOptions,
    toastId: LATEST_EVENT_TOAST_ID,
    icon: false,
    closeButton: false,
    autoClose:
      notification.type.toLowerCase() === "meeting.invitation"
        ? false
        : baseEventOptions.autoClose,
    className: ["waivera-toast waivera-event-toast", options?.className]
      .filter(Boolean)
      .join(" "),
  };

  if (isExistingEventActive) {
    toast.update(LATEST_EVENT_TOAST_ID, {
      ...mergedOptions,
      render: content,
    });
  } else {
    toast(content, mergedOptions);
  }

  return LATEST_EVENT_TOAST_ID;
};

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [callData, setCallData] = useState<{
    hubResponse: ReceiveGroupCallInviteResponse;
    onAccept: () => void;
    onReject: () => void;
  } | null>(null);

  const callSound = useMemo(() => {
    const audio = new Audio("/sounds/callingbell.mp3");
    audio.loop = true;
    return audio;
  }, []);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep this operation idempotent so call-flow boundaries can safely invoke it
  // as a fail-safe even when the incoming-call UI has already been dismissed.
  const stopCallSound = useCallback(() => {
    callSound.muted = true;
    callSound.pause();
    callSound.currentTime = 0;
  }, [callSound]);

  const notify = useCallback((message: ReactNode, options?: ToastOptions) => {
    const type = mapToastType(options?.type);

    toast(
      wrapToastContent(message as ToastContent, type),
      mergeToastOptions(options, type),
    );
  }, []);

  const notifyEvent = useCallback((
    notification: EventNotification,
    options?: ToastOptions,
  ) => {
    showEventNotificationToast(notification, options);
  }, []);

  const handleAccept = () => {
    stopCallSound();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    callData?.onAccept();
    setCallData(null);
  };

  const handleReject = () => {
    stopCallSound();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    callData?.onReject();
    setCallData(null);
  };

  const notifyForCall = (
    hubResponse: ReceiveGroupCallInviteResponse,
    onAccept: () => void,
    onReject: () => void,
  ) => {
    setCallData({ hubResponse, onAccept, onReject });

    callSound.muted = false;
    callSound.loop = true;
    callSound.play().catch((error) => {
      console.warn("Unable to autoplay incoming-call sound:", error);
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleReject();
    }, 60000);
  };

  useEffect(() => {
    if (!callData) {
      stopCallSound();
    }
  }, [callData, stopCallSound]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      stopCallSound();
    },
    [stopCallSound],
  );

  const hideCallBanner = useCallback(() => {
    setCallData(null);
    stopCallSound();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [stopCallSound]);

  return (
    <NotificationContext.Provider
      value={{
        notify,
        notifyEvent,
        notifyForCall,
        hideCallBanner,
      }}
    >
      {children}

      <ToastContainer
        position="top-right"
        autoClose={DEFAULT_AUTO_CLOSE}
        newestOnTop
        closeOnClick={false}
        pauseOnHover
        pauseOnFocusLoss={false}
        draggable
        limit={MAX_VISIBLE_TOASTS}
        stacked
        toastClassName="waivera-toast"
        toastStyle={BASE_TOAST_STYLE}
        closeButton={<WaiveraToastCloseButton />}
      />

      {callData && (
        <IncomingCallNotification
          callerId={callData.hubResponse.callerId || ""}
          callerName={callData.hubResponse.callerDisplayName}
          isMeeting={Boolean(callData.hubResponse.calenderEventId)}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
